import { useState, useEffect, useRef, useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Share2,
  Users,
  Link2,
  Copy,
  Trash2,
  Plus,
  Shield,
  Eye,
  Edit3,
  Search,
  X,
  Loader2,
} from 'lucide-react';
import { questionBankSharingApi, questionBanksApi, usersApi } from '@/api/endpoints';
import { QuestionBank } from '@/types';
import { toast } from '@/utils/toast';

interface SharedUser {
  user_id: string;
  email: string;
  name: string;
  permissions: string;
  shared_at: string;
}

interface ShareLink {
  id: string;
  token: string;
  url: string;
  permissions: string;
  created_at: string;
  expires_at?: string;
  max_uses?: number;
  use_count: number;
}

interface SearchUser {
  id: string;
  email: string;
  first_name: string;
  last_name: string;
  role: string;
}

const copyToClipboard = (text: string) => {
  if (navigator.clipboard && typeof navigator.clipboard.writeText === 'function') {
    navigator.clipboard.writeText(text);
  } else {
    const textarea = document.createElement('textarea');
    textarea.value = text;
    textarea.style.position = 'fixed';
    textarea.style.opacity = '0';
    document.body.appendChild(textarea);
    textarea.select();
    document.execCommand('copy');
    document.body.removeChild(textarea);
  }
};

export const QuestionBankSharingPage = () => {
  const queryClient = useQueryClient();
  const [selectedBankId, setSelectedBankId] = useState('');
  const [showShareModal, setShowShareModal] = useState(false);
  const [showLinkModal, setShowLinkModal] = useState(false);
  const [shareEmail, setShareEmail] = useState('');
  const [sharePermission, setSharePermission] = useState<'view' | 'edit'>('view');
  const [linkPermission, setLinkPermission] = useState<'view' | 'edit'>('view');

  // User search state
  const [userSearch, setUserSearch] = useState('');
  const [searchResults, setSearchResults] = useState<SearchUser[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const [selectedUser, setSelectedUser] = useState<SearchUser | null>(null);
  const searchRef = useRef<HTMLDivElement>(null);
  const debounceTimer = useRef<ReturnType<typeof setTimeout>>();

  // Close dropdown on outside click
  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (searchRef.current && !searchRef.current.contains(e.target as Node)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  // Debounced user search
  const searchUsers = useCallback(async (query: string) => {
    if (query.length < 2) {
      setSearchResults([]);
      setShowDropdown(false);
      return;
    }
    setIsSearching(true);
    try {
      const res = await usersApi.list({ page: 1, per_page: 50 });
      const allUsers = res?.data?.data?.data || res?.data?.data || [];
      const filtered = (allUsers as SearchUser[]).filter((u) => {
        const q = query.toLowerCase();
        return (
          u.email?.toLowerCase().includes(q) ||
          u.first_name?.toLowerCase().includes(q) ||
          u.last_name?.toLowerCase().includes(q)
        );
      });
      setSearchResults(filtered.slice(0, 10));
      setShowDropdown(true);
    } catch {
      setSearchResults([]);
    } finally {
      setIsSearching(false);
    }
  }, []);

  const handleSearchChange = (value: string) => {
    setUserSearch(value);
    setSelectedUser(null);
    setShareEmail('');
    if (debounceTimer.current) clearTimeout(debounceTimer.current);
    debounceTimer.current = setTimeout(() => searchUsers(value), 300);
  };

  const handleSelectUser = (user: SearchUser) => {
    setSelectedUser(user);
    setShareEmail(user.email);
    setUserSearch(user.email);
    setShowDropdown(false);
  };

  // Fetch question banks
  const { data: banksRes } = useQuery({
    queryKey: ['question-banks-sharing'],
    queryFn: () => questionBanksApi.list({ page: 1, per_page: 200 }),
    staleTime: 30000,
  });

  const banks: QuestionBank[] = Array.isArray(banksRes?.data?.data?.data)
    ? banksRes.data.data.data
    : Array.isArray(banksRes?.data?.data)
      ? banksRes.data.data
      : [];

  // Fetch shared users
  const { data: sharedUsersRes, isLoading: usersLoading } = useQuery({
    queryKey: ['shared-users', selectedBankId],
    queryFn: () => questionBankSharingApi.getSharedUsers(selectedBankId),
    enabled: !!selectedBankId,
    staleTime: 15000,
  });

  const sharedUsers: SharedUser[] = Array.isArray(sharedUsersRes?.data?.data)
    ? (sharedUsersRes.data.data as SharedUser[])
    : [];

  // Fetch share links
  const { data: shareLinksRes, isLoading: linksLoading } = useQuery({
    queryKey: ['share-links', selectedBankId],
    queryFn: () => questionBankSharingApi.getShareLinks(selectedBankId),
    enabled: !!selectedBankId,
    staleTime: 15000,
  });

  const shareLinks: ShareLink[] = Array.isArray(shareLinksRes?.data?.data)
    ? (shareLinksRes.data.data as ShareLink[])
    : [];

  // Share with user mutation
  const shareWithUserMutation = useMutation({
    mutationFn: () =>
      questionBankSharingApi.shareWithUser(selectedBankId, {
        email: shareEmail,
        settings: { permissions: sharePermission },
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['shared-users', selectedBankId] });
      toast.success('Question bank shared successfully');
      setShowShareModal(false);
      setShareEmail('');
      setUserSearch('');
      setSelectedUser(null);
      setSharePermission('view');
    },
    onError: (error: any) => toast.error(error?.response?.data?.message || 'Failed to share'),
  });

  // Revoke access mutation
  const revokeAccessMutation = useMutation({
    mutationFn: (userId: string) =>
      questionBankSharingApi.revokeAccess(selectedBankId, userId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['shared-users', selectedBankId] });
      toast.success('Access revoked');
    },
    onError: (error: any) => toast.error(error?.response?.data?.message || 'Failed to revoke access'),
  });

  // Create share link mutation
  const createLinkMutation = useMutation({
    mutationFn: () =>
      questionBankSharingApi.createShareLink(selectedBankId, {
        permissions: linkPermission,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['share-links', selectedBankId] });
      toast.success('Share link created');
      setShowLinkModal(false);
      setLinkPermission('view');
    },
    onError: (error: any) => toast.error(error?.response?.data?.message || 'Failed to create link'),
  });

  // Delete share link mutation
  const deleteLinkMutation = useMutation({
    mutationFn: (linkId: string) => questionBankSharingApi.deleteShareLink(linkId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['share-links', selectedBankId] });
      toast.success('Share link deleted');
    },
    onError: (error: any) => toast.error(error?.response?.data?.message || 'Failed to delete link'),
  });

  const handleCopyLink = (url: string) => {
    const fullUrl = `${window.location.origin}${url}`;
    copyToClipboard(fullUrl);
    toast.success('Link copied to clipboard');
  };

  const selectedBank = banks.find((b) => b.id === selectedBankId);

  const openShareModal = () => {
    setShareEmail('');
    setUserSearch('');
    setSelectedUser(null);
    setSharePermission('view');
    setSearchResults([]);
    setShowShareModal(true);
  };

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-white">Question Bank Sharing</h1>
          <p className="text-gray-400 mt-1 text-sm">Share question banks with other users or via links</p>
        </div>
      </div>

      {/* Bank selector */}
      <div className="bg-[#12121a]/80 backdrop-blur-sm border border-white/[0.06] rounded-xl p-5">
        <label className="block text-gray-400 text-xs font-medium uppercase tracking-wider mb-2">Select Question Bank</label>
        <select
          value={selectedBankId}
          onChange={(e) => setSelectedBankId(e.target.value)}
          className="w-full max-w-md px-4 py-2.5 bg-[#0d0d12] border border-white/[0.06] rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-teal-500/50 text-sm appearance-none"
        >
          <option value="">-- Choose a question bank --</option>
          {banks.map((bank) => (
            <option key={bank.id} value={bank.id}>
              {bank.name} {bank.subject ? `(${bank.subject})` : ''}
            </option>
          ))}
        </select>
      </div>

      {selectedBankId && (
        <>
          {/* Shared Users Section */}
          <div className="bg-[#12121a]/80 backdrop-blur-sm border border-white/[0.06] rounded-xl overflow-hidden">
            <div className="p-5 border-b border-white/[0.06] flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-teal-500/10 flex items-center justify-center">
                  <Users className="w-5 h-5 text-teal-400" />
                </div>
                <div>
                  <h3 className="text-white font-semibold">Shared Users</h3>
                  <p className="text-gray-500 text-xs">{sharedUsers.length} user{sharedUsers.length !== 1 ? 's' : ''} with access</p>
                </div>
              </div>
              <button
                onClick={openShareModal}
                className="px-4 py-2.5 bg-gradient-to-r from-teal-500 to-cyan-500 text-white rounded-xl font-medium hover:from-teal-600 hover:to-cyan-600 shadow-lg shadow-teal-500/20 transition-all flex items-center gap-2 text-sm"
              >
                <Plus className="w-4 h-4" />
                Share with User
              </button>
            </div>

            {usersLoading ? (
              <div className="flex justify-center items-center py-12">
                <Loader2 className="w-10 h-10 text-teal-400 animate-spin" />
              </div>
            ) : sharedUsers.length === 0 ? (
              <div className="text-center py-12">
                <div className="w-16 h-16 rounded-2xl bg-white/[0.04] flex items-center justify-center mx-auto mb-4">
                  <Users className="w-8 h-8 text-gray-500" />
                </div>
                <p className="text-white font-semibold text-lg mb-2">No shared users</p>
                <p className="text-gray-400 text-sm">Share &ldquo;{selectedBank?.name}&rdquo; with other users to collaborate</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-white/[0.06]">
                      <th className="text-left py-4 px-6 text-xs font-semibold text-gray-400 uppercase tracking-wider">User</th>
                      <th className="text-left py-4 px-6 text-xs font-semibold text-gray-400 uppercase tracking-wider">Permission</th>
                      <th className="text-left py-4 px-6 text-xs font-semibold text-gray-400 uppercase tracking-wider">Shared</th>
                      <th className="text-right py-4 px-6 text-xs font-semibold text-gray-400 uppercase tracking-wider">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-white/[0.04]">
                    {sharedUsers.map((user) => (
                      <tr key={user.user_id} className="hover:bg-white/[0.02] transition-colors">
                        <td className="py-4 px-6">
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-xl bg-teal-500/10 flex items-center justify-center">
                              <span className="text-sm font-bold text-teal-400">{(user.name || user.email)[0].toUpperCase()}</span>
                            </div>
                            <div>
                              <p className="text-white font-medium">{user.name}</p>
                              <p className="text-gray-500 text-xs">{user.email}</p>
                            </div>
                          </div>
                        </td>
                        <td className="py-4 px-6">
                          <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs font-medium border ${
                            user.permissions === 'edit'
                              ? 'bg-cyan-500/10 text-cyan-400 border-cyan-500/20'
                              : 'bg-teal-500/10 text-teal-400 border-teal-500/20'
                          }`}>
                            {user.permissions === 'edit' ? <Edit3 className="w-3 h-3" /> : <Eye className="w-3 h-3" />}
                            {user.permissions === 'edit' ? 'Edit' : 'View'}
                          </span>
                        </td>
                        <td className="py-4 px-6 text-gray-400 text-sm">
                          {new Date(user.shared_at).toLocaleDateString()}
                        </td>
                        <td className="py-4 px-6 text-right">
                          <button
                            onClick={() => revokeAccessMutation.mutate(user.user_id)}
                            disabled={revokeAccessMutation.isPending}
                            className="p-2 rounded-lg bg-red-500/10 text-red-400 hover:bg-red-500/20 transition-colors"
                            title="Revoke access"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Share Links Section */}
          <div className="bg-[#12121a]/80 backdrop-blur-sm border border-white/[0.06] rounded-xl overflow-hidden">
            <div className="p-5 border-b border-white/[0.06] flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-cyan-500/10 flex items-center justify-center">
                  <Link2 className="w-5 h-5 text-cyan-400" />
                </div>
                <div>
                  <h3 className="text-white font-semibold">Share Links</h3>
                  <p className="text-gray-500 text-xs">{shareLinks.length} active link{shareLinks.length !== 1 ? 's' : ''}</p>
                </div>
              </div>
              <button
                onClick={() => { setLinkPermission('view'); setShowLinkModal(true); }}
                className="px-4 py-2.5 bg-gradient-to-r from-teal-500 to-cyan-500 text-white rounded-xl font-medium hover:from-teal-600 hover:to-cyan-600 shadow-lg shadow-teal-500/20 transition-all flex items-center gap-2 text-sm"
              >
                <Plus className="w-4 h-4" />
                Create Link
              </button>
            </div>

            {linksLoading ? (
              <div className="flex justify-center items-center py-12">
                <Loader2 className="w-10 h-10 text-teal-400 animate-spin" />
              </div>
            ) : shareLinks.length === 0 ? (
              <div className="text-center py-12">
                <div className="w-16 h-16 rounded-2xl bg-white/[0.04] flex items-center justify-center mx-auto mb-4">
                  <Link2 className="w-8 h-8 text-gray-500" />
                </div>
                <p className="text-white font-semibold text-lg mb-2">No share links</p>
                <p className="text-gray-400 text-sm">Create a link to share this bank with anyone</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-white/[0.06]">
                      <th className="text-left py-4 px-6 text-xs font-semibold text-gray-400 uppercase tracking-wider">Link</th>
                      <th className="text-left py-4 px-6 text-xs font-semibold text-gray-400 uppercase tracking-wider">Permission</th>
                      <th className="text-left py-4 px-6 text-xs font-semibold text-gray-400 uppercase tracking-wider">Created</th>
                      <th className="text-right py-4 px-6 text-xs font-semibold text-gray-400 uppercase tracking-wider">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-white/[0.04]">
                    {shareLinks.map((link) => (
                      <tr key={link.id} className="hover:bg-white/[0.02] transition-colors">
                        <td className="py-4 px-6">
                          <div className="flex items-center gap-2">
                            <Link2 className="w-4 h-4 text-gray-500 flex-shrink-0" />
                            <span className="font-mono text-xs text-gray-400 truncate max-w-xs">
                              {link.url}
                            </span>
                          </div>
                        </td>
                        <td className="py-4 px-6">
                          <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs font-medium border ${
                            link.permissions === 'edit'
                              ? 'bg-cyan-500/10 text-cyan-400 border-cyan-500/20'
                              : 'bg-teal-500/10 text-teal-400 border-teal-500/20'
                          }`}>
                            {link.permissions === 'edit' ? <Edit3 className="w-3 h-3" /> : <Eye className="w-3 h-3" />}
                            {link.permissions === 'edit' ? 'Edit' : 'View'}
                          </span>
                        </td>
                        <td className="py-4 px-6 text-gray-400 text-sm">
                          {new Date(link.created_at).toLocaleDateString()}
                        </td>
                        <td className="py-4 px-6 text-right">
                          <div className="flex items-center justify-end gap-2">
                            <button
                              onClick={() => handleCopyLink(link.url)}
                              className="p-2 rounded-lg bg-teal-500/10 text-teal-400 hover:bg-teal-500/20 transition-colors"
                              title="Copy link"
                            >
                              <Copy className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => deleteLinkMutation.mutate(link.id)}
                              disabled={deleteLinkMutation.isPending}
                              className="p-2 rounded-lg bg-red-500/10 text-red-400 hover:bg-red-500/20 transition-colors"
                              title="Delete link"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      )}

      {/* Share with User Modal */}
      {showShareModal && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4" onClick={() => setShowShareModal(false)}>
          <div className="bg-[#12121a] border border-white/[0.08] rounded-2xl shadow-2xl max-w-lg w-full" onClick={(e) => e.stopPropagation()}>
            {/* Header */}
            <div className="p-6 border-b border-white/[0.06] flex items-center justify-between">
              <div>
                <h2 className="text-xl font-bold text-white">Share with User</h2>
                <p className="text-gray-400 text-sm mt-1">Grant access to &ldquo;{selectedBank?.name}&rdquo;</p>
              </div>
              <button onClick={() => setShowShareModal(false)} className="p-2 rounded-lg bg-white/[0.04] text-gray-400 hover:bg-white/[0.08] hover:text-white transition-colors">
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Body */}
            <div className="p-6 space-y-5">
              {/* User Search */}
              <div ref={searchRef} className="relative">
                <label className="block text-gray-300 text-sm font-medium mb-2">Search User</label>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
                  <input
                    type="text"
                    value={userSearch}
                    onChange={(e) => handleSearchChange(e.target.value)}
                    onFocus={() => { if (searchResults.length > 0) setShowDropdown(true); }}
                    className="w-full pl-10 pr-10 py-2.5 bg-[#0d0d12] border border-white/[0.06] rounded-xl text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-teal-500/50 text-sm"
                    placeholder="Search by name or email..."
                    autoFocus
                  />
                  {isSearching && <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500 animate-spin" />}
                  {selectedUser && !isSearching && (
                    <button
                      onClick={() => { setSelectedUser(null); setShareEmail(''); setUserSearch(''); setSearchResults([]); }}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-white transition-colors"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  )}
                </div>

                {/* Search Dropdown */}
                {showDropdown && searchResults.length > 0 && (
                  <div className="absolute z-10 w-full mt-1.5 bg-[#12121a] border border-white/[0.08] rounded-xl shadow-2xl max-h-52 overflow-y-auto">
                    {searchResults.map((user) => (
                      <button
                        key={user.id}
                        onClick={() => handleSelectUser(user)}
                        className="w-full flex items-center gap-3 px-4 py-3 hover:bg-white/[0.04] transition-colors text-left first:rounded-t-xl last:rounded-b-xl"
                      >
                        <div className="w-8 h-8 rounded-xl bg-teal-500/10 flex items-center justify-center flex-shrink-0">
                          <span className="text-xs font-bold text-teal-400">{(user.first_name || user.email)[0].toUpperCase()}</span>
                        </div>
                        <div className="min-w-0 flex-1">
                          <p className="text-white text-sm font-medium truncate">
                            {user.first_name && user.last_name ? `${user.first_name} ${user.last_name}` : user.email}
                          </p>
                          <p className="text-gray-500 text-xs truncate">{user.email}</p>
                        </div>
                        <span className="inline-flex items-center px-2 py-0.5 rounded-lg text-[10px] font-medium bg-purple-500/10 text-purple-400 border border-purple-500/20 flex-shrink-0">
                          {user.role}
                        </span>
                      </button>
                    ))}
                  </div>
                )}

                {userSearch.length >= 2 && !isSearching && searchResults.length === 0 && (
                  <div className="absolute z-10 w-full mt-1.5 bg-[#12121a] border border-white/[0.08] rounded-xl shadow-2xl p-6 text-center">
                    <p className="text-white font-medium text-sm">No users found</p>
                    <p className="text-gray-500 text-xs mt-1">Try a different search term</p>
                  </div>
                )}
              </div>

              {/* Selected User Preview */}
              {selectedUser && (
                <div className="flex items-center gap-3 p-4 bg-teal-500/5 border border-teal-500/15 rounded-xl">
                  <div className="w-10 h-10 rounded-xl bg-teal-500/10 flex items-center justify-center flex-shrink-0">
                    <span className="text-sm font-bold text-teal-400">{(selectedUser.first_name || selectedUser.email)[0].toUpperCase()}</span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-white font-medium truncate">
                      {selectedUser.first_name} {selectedUser.last_name}
                    </p>
                    <p className="text-gray-500 text-xs truncate">{selectedUser.email}</p>
                  </div>
                  <span className="inline-flex items-center px-2 py-0.5 rounded-lg text-[10px] font-medium bg-purple-500/10 text-purple-400 border border-purple-500/20">
                    {selectedUser.role}
                  </span>
                </div>
              )}

              {/* Permission Selector */}
              <div>
                <label className="block text-gray-300 text-sm font-medium mb-2">Permission Level</label>
                <div className="flex gap-3">
                  <button
                    onClick={() => setSharePermission('view')}
                    className={`flex-1 flex items-center justify-center gap-2 px-4 py-3 rounded-xl border text-sm font-medium transition-all ${
                      sharePermission === 'view'
                        ? 'border-teal-500/30 bg-teal-500/10 text-teal-400'
                        : 'border-white/[0.06] text-gray-400 hover:bg-white/[0.04]'
                    }`}
                  >
                    <Eye className="w-4 h-4" />
                    View Only
                  </button>
                  <button
                    onClick={() => setSharePermission('edit')}
                    className={`flex-1 flex items-center justify-center gap-2 px-4 py-3 rounded-xl border text-sm font-medium transition-all ${
                      sharePermission === 'edit'
                        ? 'border-cyan-500/30 bg-cyan-500/10 text-cyan-400'
                        : 'border-white/[0.06] text-gray-400 hover:bg-white/[0.04]'
                    }`}
                  >
                    <Edit3 className="w-4 h-4" />
                    Can Edit
                  </button>
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="p-6 border-t border-white/[0.06] flex gap-3">
              <button
                className="flex-1 px-4 py-2.5 bg-white/[0.04] border border-white/[0.06] text-gray-300 rounded-xl hover:bg-white/[0.08] font-medium text-sm"
                onClick={() => setShowShareModal(false)}
              >
                Cancel
              </button>
              <button
                className="flex-1 px-4 py-2.5 bg-teal-500 text-white rounded-xl hover:bg-teal-600 font-medium text-sm flex items-center justify-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed"
                onClick={() => shareWithUserMutation.mutate()}
                disabled={!shareEmail.trim() || shareWithUserMutation.isPending}
              >
                {shareWithUserMutation.isPending ? (
                  <><Loader2 className="w-4 h-4 animate-spin" />Sharing...</>
                ) : (
                  <><Share2 className="w-4 h-4" />Share</>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Create Link Modal */}
      {showLinkModal && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4" onClick={() => setShowLinkModal(false)}>
          <div className="bg-[#12121a] border border-white/[0.08] rounded-2xl shadow-2xl max-w-lg w-full" onClick={(e) => e.stopPropagation()}>
            {/* Header */}
            <div className="p-6 border-b border-white/[0.06] flex items-center justify-between">
              <div>
                <h2 className="text-xl font-bold text-white">Create Share Link</h2>
                <p className="text-gray-400 text-sm mt-1">Generate a shareable link for &ldquo;{selectedBank?.name}&rdquo;</p>
              </div>
              <button onClick={() => setShowLinkModal(false)} className="p-2 rounded-lg bg-white/[0.04] text-gray-400 hover:bg-white/[0.08] hover:text-white transition-colors">
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Body */}
            <div className="p-6 space-y-5">
              <div>
                <label className="block text-gray-300 text-sm font-medium mb-2">Permission Level</label>
                <div className="flex gap-3">
                  <button
                    onClick={() => setLinkPermission('view')}
                    className={`flex-1 flex items-center justify-center gap-2 px-4 py-3 rounded-xl border text-sm font-medium transition-all ${
                      linkPermission === 'view'
                        ? 'border-teal-500/30 bg-teal-500/10 text-teal-400'
                        : 'border-white/[0.06] text-gray-400 hover:bg-white/[0.04]'
                    }`}
                  >
                    <Eye className="w-4 h-4" />
                    View Only
                  </button>
                  <button
                    onClick={() => setLinkPermission('edit')}
                    className={`flex-1 flex items-center justify-center gap-2 px-4 py-3 rounded-xl border text-sm font-medium transition-all ${
                      linkPermission === 'edit'
                        ? 'border-cyan-500/30 bg-cyan-500/10 text-cyan-400'
                        : 'border-white/[0.06] text-gray-400 hover:bg-white/[0.04]'
                    }`}
                  >
                    <Edit3 className="w-4 h-4" />
                    Can Edit
                  </button>
                </div>
              </div>

              <div className="bg-[#0d0d12] border border-white/[0.06] rounded-xl p-4">
                <div className="flex items-start gap-3">
                  <Shield className="w-5 h-5 text-gray-500 mt-0.5 flex-shrink-0" />
                  <p className="text-gray-400 text-sm leading-relaxed">
                    Anyone with this link will have <span className="text-white font-medium">{linkPermission}</span> access to the question bank. You can delete the link at any time to revoke access.
                  </p>
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="p-6 border-t border-white/[0.06] flex gap-3">
              <button
                className="flex-1 px-4 py-2.5 bg-white/[0.04] border border-white/[0.06] text-gray-300 rounded-xl hover:bg-white/[0.08] font-medium text-sm"
                onClick={() => setShowLinkModal(false)}
              >
                Cancel
              </button>
              <button
                className="flex-1 px-4 py-2.5 bg-teal-500 text-white rounded-xl hover:bg-teal-600 font-medium text-sm flex items-center justify-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed"
                onClick={() => createLinkMutation.mutate()}
                disabled={createLinkMutation.isPending}
              >
                {createLinkMutation.isPending ? (
                  <><Loader2 className="w-4 h-4 animate-spin" />Creating...</>
                ) : (
                  <><Link2 className="w-4 h-4" />Create Link</>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
